'use client';

import {
  Breakpoint,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  IconButton,
  MenuItemButton,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetHeading,
  SheetRoot,
  SheetTrigger,
  Stack,
} from '@pie/amber';
import { IconEllipsisVertical } from '@pie/amber-icons';
import NextLink from 'next/link';
import { useState } from 'react';
import { StandardCoiDialog } from '@/components/StandardCoiDialog';
import { env } from '@/config/env';
import { isActivePolicy } from '@/hooks/usePolicy';
import { usePolicyPaymentPlan } from '@/hooks/usePolicyPaymentPlan';
import { IssuedSearchPolicy } from '@/hooks/useSearchPolicies';
import { usePolicyPaymentMethod } from '@/lib/api/billing';
import { mapPolicyPaymentMethodType } from '@/lib/api/billing/mappers';
import { hasPaymentMethod, isPayrollPaymentPlan } from '@/lib/api/billing/predicates';

type Props = {
  policy: IssuedSearchPolicy;
};

const PaymentMethodAction = ({
  policy,
  isMobile = true,
}: {
  policy: IssuedSearchPolicy;
  isMobile?: boolean;
}) => {
  const methodQuery = usePolicyPaymentMethod(policy.policyId);
  const planQuery = usePolicyPaymentPlan(policy.policyId);

  // Payroll (and other no-installment) plans are managed through the payroll
  // partner, not Pie — same gate DesktopPaymentDetails/MobilePaymentDetails
  // use to hide the payment method section on the policy page.
  if (!methodQuery.isSuccess || !planQuery.isSuccess || isPayrollPaymentPlan(planQuery.data)) {
    return null;
  }

  const mappedMethod = methodQuery.data ? mapPolicyPaymentMethodType(methodQuery.data) : null;

  const actionText =
    !mappedMethod || !hasPaymentMethod(mappedMethod) ? 'Add Payment Method' : 'Edit Payment Method';

  return isMobile ? (
    <MenuItemButton as={NextLink} href={`/billing/payment-methods/${policy.policyId}/edit`}>
      {actionText}
    </MenuItemButton>
  ) : (
    <DropdownMenuItem as={NextLink} href={`/billing/payment-methods/${policy.policyId}/edit`}>
      {actionText}
    </DropdownMenuItem>
  );
};

const MobilePolicyActionsSheet = ({ policy }: Props) => {
  const [dialog, setDialog] = useState<null | 'actions' | 'coi'>(null);

  return (
    <>
      <SheetRoot
        onOpenChange={(next) => {
          setDialog(next === true ? 'actions' : null);
        }}
        open={dialog === 'actions'}
        side="bottom"
      >
        <SheetTrigger>
          <IconButton
            emphasis="light"
            label={`Actions for ${policy.lastSegment.primaryLegalEntity.entityName}`}
            size="sm"
            title="More Actions"
            tone="accent"
          >
            <IconEllipsisVertical size="sm" />
          </IconButton>
        </SheetTrigger>
        <SheetContent closeOnOutsideInteraction>
          <SheetHeader>
            <SheetHeading>Policy Actions</SheetHeading>
            <SheetClose />
          </SheetHeader>
          <SheetBody>
            <Stack gap="layout-2xs">
              <MenuItemButton
                as="a"
                href={env.NEXT_PUBLIC_FILE_CLAIM_URL}
                rel="noopener noreferrer"
                target="_blank"
              >
                File a Claim
              </MenuItemButton>
              <MenuItemButton as={NextLink} href={`/billing?policy=${policy.policyId}`}>
                View Payment Activity
              </MenuItemButton>
              <MenuItemButton as={NextLink} href={`/policy/${policy.policyId}/documents`}>
                View Policy Documents
              </MenuItemButton>

              {isActivePolicy(policy) && (
                <MenuItemButton
                  onClick={() => {
                    setDialog('coi');
                  }}
                >
                  Create Certificate of Insurance
                </MenuItemButton>
              )}
              <PaymentMethodAction policy={policy} />
            </Stack>
          </SheetBody>
        </SheetContent>
      </SheetRoot>
      <StandardCoiDialog
        onClose={() => setDialog(null)}
        open={dialog === 'coi'}
        policyId={policy.policyId}
      />
    </>
  );
};

export const PolicyActionsMenu = ({ policy }: Props) => {
  const [dialog, setDialog] = useState<null | 'coi'>(null);

  return (
    <Breakpoint above="sm" fallback={<MobilePolicyActionsSheet policy={policy} />}>
      <DropdownMenuRoot>
        <DropdownMenuTrigger>
          <IconButton
            emphasis="light"
            label={`Actions for ${policy.lastSegment.primaryLegalEntity.entityName}`}
            size="sm"
            title="More Actions"
            tone="accent"
          >
            <IconEllipsisVertical size="sm" />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuItem
            as="a"
            href={env.NEXT_PUBLIC_FILE_CLAIM_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            File a Claim
          </DropdownMenuItem>
          <DropdownMenuItem as={NextLink} href={`/billing?policy=${policy.policyId}`}>
            View Payment Activity
          </DropdownMenuItem>
          <DropdownMenuItem as={NextLink} href={`/policy/${policy.policyId}/documents`}>
            View Policy Documents
          </DropdownMenuItem>
          {isActivePolicy(policy) && (
            <DropdownMenuItem
              onClick={() => {
                setDialog('coi');
              }}
            >
              Create Certificate of Insurance
            </DropdownMenuItem>
          )}
          <PaymentMethodAction isMobile={false} policy={policy} />
        </DropdownMenuContent>
      </DropdownMenuRoot>
      <StandardCoiDialog
        onClose={() => setDialog(null)}
        open={dialog === 'coi'}
        policyId={policy.policyId}
      />
    </Breakpoint>
  );
};
